/**
 * When the current access token stops being accepted.
 *
 * This is a plain epoch-millisecond number, not a credential — the token itself
 * lives in an httpOnly cookie that JavaScript cannot read. Persisting the
 * timestamp is what lets the proactive rotation timer survive a page reload and
 * schedule the next refresh without first provoking a 401.
 *
 * Its own module so that `client.ts` (which writes it) and `token-rotation.ts`
 * (which reads it) do not have to import each other.
 */

const STORAGE_KEY = 'epic-hub:access-token-expires-at';

let expiresAt: number | null = null;

/** localStorage throws in private-mode Safari and when storage is disabled. */
function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function getAccessTokenExpiry(): number | null {
  if (expiresAt !== null) return expiresAt;

  const stored = safeStorage()?.getItem(STORAGE_KEY);
  if (!stored) return null;

  const parsed = Number(stored);
  expiresAt = Number.isFinite(parsed) ? parsed : null;
  return expiresAt;
}

/** `expiresIn` is the access-token lifetime in seconds, straight off AuthResponseDto. */
export function setAccessTokenExpiry(expiresInSeconds: number): void {
  if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) return;

  expiresAt = Date.now() + expiresInSeconds * 1000;
  safeStorage()?.setItem(STORAGE_KEY, String(expiresAt));
}

export function clearAccessTokenExpiry(): void {
  expiresAt = null;
  safeStorage()?.removeItem(STORAGE_KEY);
}
